import React from 'react'
import client from '../apollo'
import palette from '../theme/palette'
import dayjs from './dayjs-setup'

import {
  AccessTime,
  AccountBalance,
  Apartment,
  AssignmentOutlined,
  Balance,
  CalendarMonth,
  ContactPage,
  Dashboard,
  Gavel,
  Groups,
  Insights,
  LanOutlined,
  MenuBook,
  PendingActions,
  Person,
  PersonSearch,
  ReceiptLong,
  ReceiptOutlined,
  TaskAlt,
  Timeline
} from '@mui/icons-material'
import { orange } from '@mui/material/colors'
import { Department, EntryType, PaymentMethod, User, UserRole } from '../../generated'
import {
  ADD_COST_ENTRY,
  ADD_ENTRY,
  ADD_EXPENSE_ENTRY,
  ADD_LEGAL_FEES,
  ADD_NON_CLIENT_ENTRY,
  ADD_TIME_ENTRY,
  AUTH_TOKEN,
  CLIENT_NUMBER_ALREADY_EXIST,
  CONFLICT_EXCEPTION,
  COURT_DATES_ROUTE,
  EMAIL_ALREADY_EXIST,
  EntryTabsLabel,
  FIRM_DASHBOARD_ROUTE,
  HELMER_KEYS,
  NOT_APPLICABLE,
  NOT_FOUND_EXCEPTION,
  PAYMENTS_ROUTE,
  PLEASE_ENTER_VALID_PHONE_NUMBER,
  SOMETHING_WENT_WRONG,
  stepFields,
  TIME_FORMAT_HH_MM_REGEX,
  TIME_FORMAT_HH_REGEX
} from '../constants'
import {
  SESSION_CLOCK_SKEW,
  SESSION_ENDED_EVENT,
  SESSION_END_REASON,
  SessionEndReason,
  SessionEndedDetail,
  getStoredToken
} from './session'

import { FieldErrors, FieldValues, UseFormSetValue } from 'react-hook-form'
import { CasesLogo, ChartBar } from '../assets/svgs'
import { Action, ActionType } from '../context/AppContextReducer'

import { Alert } from '../components/common/Alert'
import { CreateCaseCustomInput } from '../interfaceTypes'
import { format } from 'date-fns'

const clearSession = () => {
  localStorage.removeItem(AUTH_TOKEN)
  localStorage.removeItem(SESSION_CLOCK_SKEW)
  localStorage.removeItem(HELMER_KEYS.FORM)
  localStorage.removeItem(HELMER_KEYS.TIMER)
  client.clearStore()
}

export const handleLogout = () => {
  localStorage.removeItem(SESSION_END_REASON)
  clearSession()
}

/**
 * Ends the session without the user asking: clears it, then lets SessionWatcher take
 * the app to the login page. Safe to call from outside React.
 */
export const endSession = (reason: SessionEndReason) => {
  // Every request in flight when the session ends fails the same way; only the first one acts.
  if (!getStoredToken()) return

  // The reason goes in before the token comes out: other tabs read it on the token's removal,
  // and storage changes reach them in the order they were made.
  localStorage.setItem(SESSION_END_REASON, reason)
  // USER_EMAIL stays for SessionWatcher, which reads whose session it was and then removes it.
  clearSession()
  window.dispatchEvent(new CustomEvent<SessionEndedDetail>(SESSION_ENDED_EVENT, { detail: { reason } }))
}

export * from './session'

export const getIsTimerRunning = () => {
  const stored = localStorage.getItem(HELMER_KEYS.TIMER)
  if (stored) {
    const timer = JSON.parse(stored || '{}')
    return timer?.startTime ? !timer.isPaused : true
  }
  return false
}
export const deleteTimerKeys = () => {
  localStorage.removeItem(HELMER_KEYS.FORM)
  localStorage.removeItem(HELMER_KEYS.TIMER)
}
export const requiredMessage = (fieldName: string) => `${fieldName} is required.`
export const titleize = (value: string) => value?.charAt(0).toUpperCase() + value?.slice(1).toLowerCase()
export const capitalizeCase = (value: string) =>
  value
    ?.split(' ')
    .map(sub => sub.charAt(0).toUpperCase() + sub.slice(1))
    .join(' ')
export const removeUnderScore = (value: string) => value?.split('_').join(' ')

export function stringToColor(string: string) {
  let hash = 0
  let i

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash)
  }

  let color = '#'

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff
    color += `00${value.toString(16)}`.slice(-2)
  }
  /* eslint-enable no-bitwise */

  return color
}

export function stringAvatar(name: string) {
  return {
    sx: {
      bgcolor: stringToColor(name)
    },
    children: `${name.split(' ')[0][0]}${name?.split(' ')[1][0]}`
  }
}

export const badgeBgColor = (roleSlice: string) => {
  switch (roleSlice) {
    case 'P':
      return palette.green

    case 'A':
      return orange[500]

    case 'I':
      return palette.wardGray

    default:
      return palette.primary.main
  }
}

// Job titles first, admin/super-admin last: those grant access, they are not anyone's job.
const ROLE_PRIORITY: UserRole[] = [
  UserRole.Attorney,
  UserRole.Paralegal,
  UserRole.Investigator,
  UserRole.Accountant,
  UserRole.Staff,
  UserRole.Dcpp,
  UserRole.Admin,
  UserRole.SuperAdmin
]

/**
 * The single role a user should be shown as. Null only if they have no roles.
 *
 * `roles` comes back in no set order, so `roles[0]` is a coin flip for anyone holding two roles.
 * Rank them instead of taking the first.
 */
export const getPrimaryRole = (roles?: ({ role?: UserRole | null } | null)[] | null): UserRole | null => {
  const held = new Set((roles || []).map(item => item?.role).filter(Boolean) as UserRole[])
  return ROLE_PRIORITY.find(role => held.has(role)) ?? null
}

/**
 * True when a user has no job role — admin/super-admin only. That is 15 accounts, mostly real staff
 * who were never given a job role, and all of them were already excluded before this. To make one of
 * them assignable, give them their job role rather than loosening this check.
 *
 * A user with no roles at all is not counted as system-only, matching the old behaviour.
 */
export const isSystemOnlyUser = (roles?: ({ role?: UserRole | null } | null)[] | null): boolean => {
  const primaryRole = getPrimaryRole(roles)
  return primaryRole === UserRole.Admin || primaryRole === UserRole.SuperAdmin
}

export const getUserRole = (role: string): UserRole | null => {
  switch (role) {
    case UserRole.Admin:
      return UserRole.Admin

    case UserRole.Attorney:
      return UserRole.Attorney

    case UserRole.Investigator:
      return UserRole.Investigator

    case UserRole.Paralegal:
      return UserRole.Paralegal

    case UserRole.SuperAdmin:
      return UserRole.SuperAdmin

    default:
      return null
  }
}

export const returnErrorMessage = (message: string) => {
  switch (message) {
    case NOT_FOUND_EXCEPTION:
      Alert.error(PLEASE_ENTER_VALID_PHONE_NUMBER)
      break

    case CLIENT_NUMBER_ALREADY_EXIST:
      Alert.error(CLIENT_NUMBER_ALREADY_EXIST)
      break

    case CONFLICT_EXCEPTION:
      Alert.error(EMAIL_ALREADY_EXIST)
      break

    default:
      Alert.error(SOMETHING_WENT_WRONG)
      break
  }
}

export const exampleMessage = (e: string) => `i.e ${e}`
export const maxLength = (fieldName: string, length: number) => `${fieldName} can be up to ${length} characters long.`
export const minLength = (fieldName: string, length: number) =>
  `${fieldName} should be at least ${length} characters long.`
export const validMessage = (fieldName: string, example?: string) =>
  `Please enter valid ${fieldName.toLowerCase()}. ${example ? exampleMessage(example) : ''}`

/**
 * The roles that own the Accounting menu.
 *
 * Every Accounting page except the Client Ledger is gated on this. The group used to be hidden
 * wholesale from attorneys, paralegals, investigators and staff, so several of its items carried
 * no check of their own — Deposit Funds was plainly `true` — and opening the group up for the
 * Client Ledger would otherwise have handed those roles the rest of Accounting with it.
 */
const canAccessAccountingPages = (isAdmin?: boolean, isAccountant?: boolean, isSuperAdmin?: boolean) =>
  !!(isAdmin || isSuperAdmin || isAccountant)

export const getNavItems = (
  isParalegal: boolean,
  isAttorney: boolean,
  isInvestigator: boolean,
  isAdmin: boolean,
  isStaff?: boolean,
  isAccountant?: boolean,
  isSuperAdmin?: boolean
) => [
  {
    label: 'Dashboard',
    icon: React.createElement(Dashboard),
    path: '/dashboard',
    visible: isParalegal || isAttorney
  },
  // Firm Dashboard is hidden from the nav for now; the page still works at FIRM_DASHBOARD_ROUTE
  // for admins and accounting. Restore this item (and its two imports above) to show it again.
  {
    label: 'Firm Dashboard',
    icon: React.createElement(Insights),
    path: FIRM_DASHBOARD_ROUTE,
    visible: !!(isAdmin || isSuperAdmin || isAccountant)
  },
  {
    label: 'Conflict Checks',
    icon: React.createElement(PersonSearch),
    path: '/conflict-checks',
    visible: true
  },

  {
    label: 'Consults',
    icon: React.createElement(AssignmentOutlined),
    path: '/consults',
    visible: true
  },

  {
    label: 'Matters',
    icon: React.createElement(CasesLogo),
    path: '/matters?activeTab=0&currentPage=1',
    visible: true
  },

  {
    label: 'Court Dates',
    icon: React.createElement(CalendarMonth),
    path: COURT_DATES_ROUTE,
    // Same roles the API serves (HCK-262, HCK-270): admins firm-wide, attorneys their own Resp 1 matters,
    // paralegals the matters they are assigned to or created
    visible: !!(isAdmin || isSuperAdmin || isAttorney || isParalegal)
  },

  {
    label: 'Contacts',
    icon: React.createElement(Groups),
    path: `/contacts/client`,
    visible: true,
    subItems: [
      {
        label: 'Clients',
        path: '/contacts/client',
        visible: true
      },
      {
        label: 'Contacts',
        path: '/contacts/contact',
        visible: true
      },
      {
        label: 'Adversaries',
        path: '/contacts/adversary',
        visible: true
      },
      {
        label: 'Judges',
        path: '/contacts/judge',
        visible: true
      }
    ]
  },
  {
    label: 'Custom Tasks',
    icon: React.createElement(TaskAlt),
    path: '/custom-tasks',
    visible: true
  },

  {
    label: 'Time Keepers',
    icon: React.createElement(AccessTime),
    path: `/entries/${isInvestigator ? 'cost' : 'time'}`,
    visible: true,
    subItems: [
      {
        label: 'All Entries',
        path: '/entries/dashboard',
        visible: !!(isAdmin || isAccountant || isSuperAdmin)
      },
      {
        label: 'Time Entries',
        path: '/entries/time',
        visible: !isInvestigator && !isStaff
      },
      {
        label: 'Legal Fees',
        path: '/entries/legal',
        visible: !isStaff
      },
      {
        label: 'Expense Entries',
        path: '/entries/expense',
        visible: !isStaff
      },
      {
        label: 'Investigator Time',
        path: '/entries/cost',
        visible: !isAttorney && !isStaff
      },
      {
        label: 'Non Client Entries',
        path: '/entries/non-client',
        visible: true
      },
      {
        label: 'Interest Entries',
        path: '/entries/interest',
        visible: !!(isAdmin || isAccountant || isSuperAdmin)
      }
    ]
  },
  {
    label: 'Billing',
    icon: React.createElement(ReceiptOutlined),
    path: `/billing/pre-bills`,
    visible: !isInvestigator && !isStaff,
    subItems: [
      {
        label: 'Pre Bills',
        path: '/billing/pre-bills',
        visible: !isInvestigator
      },
      {
        label: 'Bills',
        path: '/billing/bills',
        visible: true
      },
      {
        label: 'Fund Requests',
        path: '/billing/fund-requests',
        visible: true
      },
      {
        label: 'Record Payment',
        path: '/billing/record-payment',
        visible: !!(isAccountant || isAdmin || isSuperAdmin)
      },
      {
        label: 'Print Bills',
        path: '/billing/print-jobs',
        visible: !!(isAccountant || isAdmin || isSuperAdmin)
      }
    ]
  },
  {
    label: 'Accounting',
    icon: React.createElement(AccountBalance),
    // Everyone gets the Accounting menu now, but most of them only for the Client Ledger — so the
    // group header has to land somewhere they are allowed to be.
    path: canAccessAccountingPages(isAdmin, isAccountant, isSuperAdmin)
      ? '/accounts/chartered'
      : '/accounts/client-ledger',
    visible: true,
    subItems: [
      {
        label: 'Charts of Accounts',
        path: '/accounts/chartered',
        visible: canAccessAccountingPages(isAdmin, isAccountant, isSuperAdmin)
      },
      // {
      //   label: 'Accounts Receivable',
      //   path: '/accounts/receivables',
      //   visible: !isAttorney && !isInvestigator
      // },
      {
        label: 'Accounts Payable',
        path: '/accounts/payable',
        visible: canAccessAccountingPages(isAdmin, isAccountant, isSuperAdmin),
        // subItems: [
          // {
          //   label: 'Payables',
          //   path: '/accounts/payable',
          //   visible: true
          // },
          // {
          //   label: 'Refunds',
          //   path: '/accounts/payable-refunds',
          //   visible: true
          // }
        // ]
      },
      {
        label: 'Deposit Funds',
        path: '/accounts/deposit-fund',
        visible: canAccessAccountingPages(isAdmin, isAccountant, isSuperAdmin)
      },
      {
        label: 'Payments',
        path: PAYMENTS_ROUTE,
        visible: canAccessAccountingPages(isAdmin, isAccountant, isSuperAdmin)
      },
      {
        label: 'Journals',
        path: '/accounts/journals',
        visible: canAccessAccountingPages(isAdmin, isAccountant, isSuperAdmin)
      },
      {
        label: 'General Ledger',
        path: '/accounts/ledger',
        visible: canAccessAccountingPages(isAdmin, isAccountant, isSuperAdmin)
      },
      {
        label: 'Client Ledger',
        path: '/accounts/client-ledger',
        visible: true
      },
      {
        label: 'Bank Reconciliation',
        icon: React.createElement(AccountBalance),
        path: '/accounts/bank-reconciliation',
        visible: canAccessAccountingPages(isAdmin, isAccountant, isSuperAdmin),
        subItems: [
          {
            label: 'Reconciliation',
            path: '/accounts/bank-reconciliation',
            visible: true
          },
          {
            label: 'History',
            path: '/accounts/bank-reconciliation/history',
            visible: true
          }
        ]
      },
      {
        label: 'Period Close',
        path: '/accounts/period-close',
        visible: !!(isAccountant || isAdmin || isSuperAdmin)
      },
      {
        label: 'Batch Emails',
        path: '/accounts/batch-emails',
        visible: !!(isAccountant || isAdmin || isSuperAdmin)
      }
    ]
  },

  {
    label: 'Reports',
    icon: React.createElement(ChartBar),
    path: '/reports',
    // Every role is allowed in (HCK-221); the API decides which reports each one sees.
    visible: true
  },
  {
    label: 'Docs & Training',
    icon: React.createElement(MenuBook),
    path: '/docs',
    visible: true
  }
]

export const getSettingsItem = (
  isParalegal: boolean,
  isAttorney: boolean,
  isInvestigator: boolean,
  isAdmin: boolean,
  isSuperAdmin?: boolean
) => [
  {
    label: 'Activity Tracking',
    icon: React.createElement(Timeline),
    path: '/activity-tracking',
    visible: !isParalegal && !isAttorney && !isInvestigator
  },
  {
    label: 'Background Jobs',
    icon: React.createElement(PendingActions),
    path: '/admin/background-jobs',
    visible: !!(isAdmin || isSuperAdmin)
  },
  {
    label: 'Users',
    icon: React.createElement(Person),
    path: '/users',
    visible: !!(isAdmin || isSuperAdmin)
  },
  {
    label: 'Departments',
    icon: React.createElement(LanOutlined),
    path: '/departments',
    visible: !isParalegal && !isAttorney && !isInvestigator
  },
  {
    label: 'Vendors',
    icon: React.createElement(ContactPage),
    path: '/vendors',
    visible: !isInvestigator
  },
  {
    label: 'Profit Centers',
    icon: React.createElement(Apartment),
    path: '/profit-centers',
    visible: !isParalegal && !isAttorney && !isInvestigator
  },
  {
    label: 'Referrals',
    icon: React.createElement(ReceiptLong),
    path: '/referrals/sources',
    visible: !isParalegal && !isAttorney && !isInvestigator,
    subItems: [
      {
        label: 'Sources',
        path: '/referrals/sources',
        visible: !isParalegal && !isAttorney && !isInvestigator
      },
      {
        label: 'Types',
        path: '/referrals/types',
        visible: !isParalegal && !isAttorney && !isInvestigator
      }
    ]
  },
  {
    label: 'Law Type',
    icon: React.createElement(Balance),
    path: '/law-type',
    visible: !isInvestigator
  },
  {
    label: 'Courts',
    icon: React.createElement(Gavel),
    path: '/courts',
    visible: !isInvestigator
  }
]

export const minutesToDuration = (time: number, inSeconds = false) => {
  const totalSeconds = Math.floor(time / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  let duration: string = ''
  duration = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
  if (inSeconds) duration += `:${String(seconds).padStart(2, '0')}`
  return duration
}

/** Durations may carry seconds (`2:30:45`) — the timer writes them and the
 *  format accepts them — so they have to count, not be dropped on the floor. */
export const durationToMinutes = (duration: string): number => {
  const [hours = 0, minutes = 0, seconds = 0] = duration.split(':').map(Number)
  return hours * 60 + minutes + seconds / 60
}

/**
 * Tidies up what the user typed when they leave the Duration field: `.5` becomes
 * `0.5`, a bare `2` becomes `2:00`.
 *
 * Anything it does not recognise is left EXACTLY as typed. It used to blank the
 * field instead, which destroyed the input before the form's validation could
 * point at it — from the user's side a typo simply made the field empty itself
 * with no explanation. Reporting the problem is the schema's job now
 * (`durationField` in validationSchema), so this only ever normalises.
 */
export const handleDuration = (value: string, setValue: UseFormSetValue<FieldValues>) => {
  if (!value) return

  const isHoursAndMinutes = TIME_FORMAT_HH_MM_REGEX.test(value)
  const isDecimalHours = TIME_FORMAT_HH_REGEX.test(value)
  if (!isHoursAndMinutes && !isDecimalHours) return

  // Re-validate after normalising so a value that was flagged while half-typed
  // (e.g. `.5`) clears its error as soon as it becomes valid.
  const normalize = (next: string) => setValue('duration', next, { shouldValidate: true })

  if (value.startsWith('.')) normalize(`0${value}`)
  else if (isHoursAndMinutes && !value.includes(':')) normalize(`${value}:00`)
}

export const convertToDate = (date: number | string) => new Date(Number(date)).toISOString()

export const getDateWithTz = (date: Date | string | dayjs.Dayjs) => dayjs(date).format('YYYY-MM-DDTHH:mm:ssZ')

export const resetEntryType = (reducerDispatch: (value: Action) => void) => {
  reducerDispatch({ type: ActionType.SET_TIME_ENTRY, value: false })
  reducerDispatch({ type: ActionType.SET_IS_EXPENSE_ENTRY, value: false })
  reducerDispatch({ type: ActionType.SET_COST_ENTRY, value: false })
  reducerDispatch({ type: ActionType.SET_LEGAL_FEE, value: false })
  reducerDispatch({ type: ActionType.SET_NON_CLIENT_ENTRY, value: false })
}

export const getEntry = (type: string | EntryType) => {
  switch (type) {
    case EntryType.Time:
      return {
        type: EntryType.Time,
        label: EntryTabsLabel.TIME_ENTRIES,
        title: ADD_TIME_ENTRY
      }
    case EntryType.Expense:
      return {
        type: EntryType.Expense,
        label: EntryTabsLabel.EXPENSE_ENTRIES,
        title: ADD_EXPENSE_ENTRY
      }
    case EntryType.Cost:
      return {
        type: EntryType.Cost,
        label: EntryTabsLabel.COST_ENTRIES,
        title: ADD_COST_ENTRY
      }
    case EntryType.Legal:
      return {
        type: EntryType.Legal,
        label: EntryTabsLabel.LEGAL_FEES,
        title: ADD_LEGAL_FEES
      }
    case EntryType.NonClient:
      return {
        type: EntryType.NonClient,
        label: EntryTabsLabel.NON_CLIENT_ENTRIES,
        title: ADD_NON_CLIENT_ENTRY
      }
    default:
      return {
        type: EntryType.Time,
        label: ADD_ENTRY
      }
  }
}

export const hasAdminAccess = (user: User | null) => {
  if (!user) return false

  const adminRoles = [UserRole.SuperAdmin, UserRole.Admin, UserRole.Accountant]
  return user.roles?.some(userRole => userRole?.role && adminRoles.includes(userRole?.role))
}

/**
 * Attorneys may run the reports that can be scoped to a single responsible attorney, only ever
 * scoped to themselves — see `canRunReport` on the API and the locked Resp 1 field in
 * ReportCustomization. (Reports open to all roles, such as Matter List by Court, are not scoped.)
 *
 * The admin check comes first deliberately: a user who ALSO holds Admin/Super Admin/Accountant
 * keeps the unrestricted behaviour, so this scoping can never take access away from someone who
 * already had it.
 */
export const isAttorneyScopedUser = (user: User | null) => {
  if (!user || hasAdminAccess(user)) return false

  return !!user.roles?.some(userRole => userRole?.role === UserRole.Attorney)
}

/**
 * Paralegals may run Matter List by Attorney / Paralegal only for the matters where they are the
 * Paralegal (HCK-292) — see `ownMattersScopeField` on the API. A paralegal who is also an attorney is
 * held to Resp 1 instead (attorney-scoped), and one who is also an admin/accountant is not scoped.
 */
export const isParalegalScopedUser = (user: User | null) => {
  if (!user || hasAdminAccess(user) || isAttorneyScopedUser(user)) return false

  return !!user.roles?.some(userRole => userRole?.role === UserRole.Paralegal)
}

/**
 * Every role except super-admin/admin/accountant gets a narrowed Reports module: only the reports the
 * API offers them (`canRunReport`), and a history limited to the reports they generated themselves.
 */
export const isReportsRestrictedUser = (user: User | null) => !!user && !hasAdminAccess(user)

/** Every signed-in user may open Reports (HCK-221); which reports they see is decided by the API. */
export const canAccessReports = (user: User | null) => !!user

export const formatPhone = (phone: string) => phone.replace(/^\+1(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3')

export function formatUSPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '')

  if (digits.length !== 10) return phone

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

export const formatPhoneHyphenated = (value: string | null | undefined): string => {
  if (value == null || value === '') return ''
  const digits = value.replace(/\D/g, '')
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  return digits || value
}

export const formatCurrency = (amount: number) => {
  if (isNaN(amount)) return '0.00'

  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  })
}

export const formatCamelCaseText = (text: string): string => {
  // Handle empty or null input
  if (!text) return ''

  // Split by capital letters and numbers
  const words = text.split(/(?=[A-Z0-9])/)

  // Capitalize first letter of first word, rest lowercase
  const formattedWords = words.map((word, index) => {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  })

  // Join with spaces
  return formattedWords.join(' ')
}

export const persistTimerState = (state: { startTime: number | null; isPaused: boolean; elapsedTime: number }) => {
  localStorage.setItem(HELMER_KEYS.TIMER, JSON.stringify(state))
}

export const calculateDiscount = (amount: number, percentage: number) => {
  return amount * (percentage / 100)
}

export const formatToTwoDecimals = (num: number) => {
  return Number(num.toFixed(2))
}

export const removeUnderScoreOrHyphenAndToLowerCase = (value: string) =>
  value.split(/[_-]/).join(' ').toLocaleLowerCase()

export const enumToLabel = (enumValue: string) => {
  return capitalizeCase(removeUnderScoreOrHyphenAndToLowerCase(enumValue))
}

export const formatDateTime = (dateString: string | number) => {
  if (!dateString) return NOT_APPLICABLE
  let dateNum = dateString
  if (typeof dateString === 'string' && /^\d+$/.test(dateString)) {
    dateNum = parseInt(dateString, 10)
  }
  return dayjs(dateNum).isValid() ? dayjs(dateNum).format('MM/DD/YYYY h:mm A') : NOT_APPLICABLE
}

export function getUTCStartOfMonth(date = new Date()) {
  const userTz = dayjs.tz.guess()
  return dayjs(date).tz(userTz).startOf('month').format()
}

export function getUTCEndOfMonth(date = new Date()) {
  const userTz = dayjs.tz.guess()
  return dayjs(date).tz(userTz).endOf('month').format()
}

export const getProperPaymentMethod = (method: PaymentMethod) => {
  switch (method) {
    case PaymentMethod.Cheque:
      return 'Check'
    case PaymentMethod.Ach:
      return 'ACH'
    case PaymentMethod.Eft:
      return 'EFT'
    case PaymentMethod.Card:
      return 'Card'
    case PaymentMethod.Cash:
      return 'Cash'
    case PaymentMethod.ApplePay:
      return 'Apple Pay'
    case PaymentMethod.WireTransfer:
      return 'Wire Transfer'
    default:
      return 'Other'
  }
}

/**
 * Party fields whose error subtree can belong to two different steps. Matter Parties owns the party itself
 * (who they are and their matter role); Bill Delivery and Bill Split, which live on the BILLING step, write
 * their values into the very same objects.
 */
const PARTY_FIELDS = ['client', 'clientUsers', 'adversaryUsers']

/** Keys under a party field that are edited in Bill Delivery / Bill Split, i.e. on the Billing step. */
const BILLING_OWNED_PARTY_KEYS = [
  'billingAddress',
  'billingEmail',
  'billingPhoneNumber',
  'billDeliveryMethods',
  'splitBillShare'
]

/**
 * Works out which step a party field's errors actually belong to.
 *
 * Choosing "Paper" in Bill Delivery makes `client.billingAddress` required, but that value is nested under
 * `client`, which `stepFields` assigns to Matter Parties. Flagging the whole field sent the user to Matter
 * Parties — a step where the input does not exist and no message renders, because that form only surfaces
 * errors on the party's `id`. The address field itself is on the Billing step.
 *
 * So the subtree is split: a `billingAddress`-style key counts as a Billing error, anything else (a missing
 * party, a missing matter role, the array-level uniqueness test) stays a Matter Parties error. When both are
 * present both steps are flagged, and the caller's "first step wins" ordering is unchanged.
 */
const splitPartyErrors = (node: unknown): { party: boolean; billing: boolean } => {
  let party = false
  let billing = false

  const visit = (current: unknown) => {
    if (!current || typeof current !== 'object') return
    if (Array.isArray(current)) {
      current.forEach(visit)
      return
    }
    // An error carried by the field itself — a required party, or an array-level test such as the
    // unique-matter-role check.
    if (typeof (current as { message?: unknown }).message === 'string') party = true

    for (const [key, value] of Object.entries(current)) {
      // react-hook-form's own metadata on an error node, not nested fields.
      if (['message', 'type', 'types', 'ref'].includes(key)) continue
      if (!value || typeof value !== 'object') continue
      if (BILLING_OWNED_PARTY_KEYS.includes(key)) billing = true
      else visit(value)
    }
  }

  visit(node)
  return { party, billing }
}

export const invalidMatterClick = (errors: FieldErrors<CreateCaseCustomInput>) => {
  const stepErrorMap: { [key: number]: string[] } = {}
  stepFields.forEach((step, idx) => {
    const errorFields = step.fields.filter(field => {
      // Support nested fields (e.g. billing.billingType)
      const fieldParts = field.split('.')
      let errorObj: any = errors
      for (const part of fieldParts) {
        if (errorObj && errorObj[part]) {
          errorObj = errorObj[part]
        } else {
          errorObj = null
          break
        }
      }
      if (!errorObj) return false
      // Bill-delivery errors on a party are the Billing step's, not this one's (added just below).
      if (PARTY_FIELDS.includes(field)) return splitPartyErrors(errorObj).party
      return true
    })
    if (errorFields.length > 0) {
      stepErrorMap[idx] = errorFields
    }
  })

  // Attribute bill-delivery / split-bill errors on the party fields to the step that renders those inputs.
  const billingStepIdx = stepFields.findIndex(step => step.fields.includes('billing'))
  if (billingStepIdx >= 0) {
    const billingOwned = PARTY_FIELDS.filter(
      field => splitPartyErrors((errors as Record<string, unknown>)[field]).billing
    )
    if (billingOwned.length) {
      stepErrorMap[billingStepIdx] = [...(stepErrorMap[billingStepIdx] || []), ...billingOwned]
    }
  }

  // Find the first step with errors
  const firstErrorStep = Object.keys(stepErrorMap)
    .map(Number)
    .sort((a, b) => a - b)[0]

  return firstErrorStep
}

export const formatInterestRate = (rate: number) => {
  return `${rate * 100}%`
}
export const isDepartmentHead = (user: User | null, departments: Department[]): boolean => {
  if (!user) return false

  // Check if user is the head of any department
  return departments?.some(dept => dept.departmentHeadId === user.id) ?? false
}

export function decimalHoursToHHMM(hour: string): string {
  const decimalHours = parseFloat(hour)
  // Handle negative values or invalid input
  if (decimalHours < 0 || !isFinite(decimalHours)) {
    throw new Error('Decimal hours must be a positive finite number')
  }

  // Extract whole hours
  const hours = Math.floor(decimalHours)

  // Extract decimal part and convert to minutes
  const decimalPart = decimalHours - hours
  const minutes = Math.round(decimalPart * 60)

  // Handle rounding that might push minutes to 60
  if (minutes === 60) {
    return `${String(hours + 1).padStart(2, '0')}:00`
  }

  // Format with leading zeros
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

export const removeCountryCodeFromExternalUserPhone = (phoneNumber: string) => {
  return phoneNumber?.at(0) === '+' ? phoneNumber?.substring(2) : phoneNumber
}

export const formatAsEndOfDay = (date: Date | string): string => {
  return format(new Date(date), 'yyyy-MM-dd') + ' 23:59:59.999Z'
}

export const formatAsStartOfDay = (date: Date | string): string => {
  return format(new Date(date), 'yyyy-MM-dd') + ' 00:00:00.000Z'
}

export const generateUniqueId = () => `id-${Math.random().toString(16).slice(2)}`

/**
 * Copies text to the clipboard, returning whether it worked so the caller can report honestly
 * rather than claiming success on a silent failure.
 *
 * `navigator.clipboard` only exists in a secure context (HTTPS or localhost) and can still be
 * refused by permissions policy, so the legacy hidden-textarea path is kept as a fallback —
 * without it, a dev build opened over a plain-HTTP LAN address throws on an undefined API.
 */
export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // Non-secure context or permission denied — fall through to the legacy path.
  }

  try {
    const textArea = document.createElement('textarea')
    textArea.value = text
    // Fixed and off-screen, and readonly so focusing it cannot pop a keyboard on touch devices.
    textArea.style.position = 'fixed'
    textArea.style.top = '-9999px'
    textArea.setAttribute('readonly', '')
    document.body.appendChild(textArea)
    textArea.select()
    const copied = document.execCommand('copy')
    document.body.removeChild(textArea)
    return copied
  } catch {
    return false
  }
}
